import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { AthleteStatus } from "./athleteTypes";

export type QOption = { value: string; label: string };
export type QQuestion = {
  id: string;
  type: string;
  label: string;
  options: QOption[];
  min: number | null;
  max: number | null;
  required?: boolean;
  why_we_ask?: string;
  show_if?: Record<string, string[]>;
};
export type QSection = { id: string; label: string; questions: QQuestion[] };
export type QSchema = { sections: QSection[]; lang: string };
export type QAnswers = {
  answers: Record<string, any>;
  completed_at: string | null;
  /** W3.18 — coach-flagged questions awaiting clarification.
   * Map of qid → flag metadata. Empty when nothing flagged. */
  flagged_for_review?: Record<string, unknown>;
  /** Schema version the athlete's questionnaire was completed against. */
  schema_version?: number;
  /** Current SCHEMA_VERSION on the server — comparing the two yields needs_review. */
  current_schema_version?: number;
  /** W3.21 — true when stored schema_version < current_schema_version AND
   * the questionnaire is completed. Powers the dashboard banner. */
  needs_review?: boolean;
};
export type PrefillField = {
  value: unknown;
  source: string;
  confidence: "high" | "medium" | "low";
};
/** Шаг #10 — one source's contribution to the prefill summary. */
export type PrefillSourceGroup = {
  source: string;
  fields: string[];
};
export type QuestionnairePrefill = {
  fields: Record<string, PrefillField>;
  /** Шаг #10 — pre-grouped view for the «what we already know» modal.
   * Empty list when nothing's known yet → modal suppresses itself. */
  groups?: PrefillSourceGroup[];
  /** True when the athlete has an active intervals.icu OAuth token. */
  intervals_connected?: boolean;
  /** Athlete's previously-answered main_sport ("sport.run" etc) or null. */
  sport_hint?: string | null;
};

export function useQuestionnaireSchema() {
  return useQuery<QSchema>({
    queryKey: ["q-schema"],
    queryFn: async () => (await api.get<QSchema>("/athlete/profile/questionnaire/schema")).data,
    staleTime: 5 * 60_000,
  });
}

export function useQuestionnaireAnswers() {
  return useQuery<QAnswers>({
    queryKey: ["q-answers"],
    queryFn: async () => (await api.get<QAnswers>("/athlete/profile/questionnaire/answers")).data,
    staleTime: 30_000,
  });
}

export function useQuestionnairePrefill() {
  return useQuery<QuestionnairePrefill>({
    queryKey: ["q-prefill"],
    queryFn: async () =>
      (await api.get<QuestionnairePrefill>("/athlete/profile/questionnaire/prefill")).data,
    // Retro-3 R3-D — staleTime lowered from 5 min to 60 s, plus
    // refetchOnMount=always. The original assumption was «Athlete row
    // rarely changes, hammer-cache freely» but Шаг #10 introduced the
    // prefill summary modal whose copy is gated by
    // `intervals_connected`. Athletes who click «Connect intervals.icu»
    // from the modal and finish OAuth in <5 min came back to a STALE
    // «not connected» CTA. 60 s strikes a balance for background
    // refetches (focus, network reconnect), and `refetchOnMount:
    // "always"` guarantees that re-navigating to the questionnaire
    // page after the OAuth round-trip always pulls fresh state — the
    // stale value renders instantly from cache while the network
    // refresh streams in behind it. Mutations elsewhere (PATCH
    // /sport-metrics, DELETE /intervals) don't need to invalidate
    // explicitly because the next page mount catches them.
    staleTime: 60_000,
    refetchOnMount: "always",
  });
}

export function usePatchAnswers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (answers: Record<string, any>) => {
      const r = await api.patch<QAnswers>("/athlete/profile/questionnaire/answers", { answers });
      return r.data;
    },
    onSuccess: (data) => qc.setQueryData(["q-answers"], data),
  });
}

// ─────────────────────────────────────────────────────────────────────
// Шаг #13 — Athlete-side history visibility.
//
// Mirrors the admin coach view: athlete sees who changed what + reason
// (DSGVO Art.15 transparency). Backend endpoint added in audit pass.
// Drives the `<AthleteHistoryDrawer>` + `<RecentCoachEditsBanner>`.
// ─────────────────────────────────────────────────────────────────────

export type AthleteHistoryRevision = {
  id: number;
  changed_at: string | null;
  changed_by_user_id: number | null;
  /** "athlete" | "coach" | "admin" | "system" — surfaced verbatim from
   * questionnaire_answer_history; the UI maps to a translated actor
   * label. */
  changed_by_role: string;
  fields_changed: string[];
  /** A9 / Шаг #11 — free-text reason attached at edit time (coach typed
   * in QuestionnaireSaveModal). NULL for legacy revisions or skipped. */
  reason: string | null;
};

export type AthleteHistoryResponse = {
  revisions: AthleteHistoryRevision[];
  /** Total revisions stored server-side; UI can render «5 of 47»
   * without paginating. Cheap COUNT(*) over an indexed FK. */
  total_known: number;
};

export function useAthleteQuestionnaireHistory(opts: {
  limit?: number;
  offset?: number;
  enabled?: boolean;
} = {}) {
  const { limit = 50, offset = 0, enabled = true } = opts;
  return useQuery<AthleteHistoryResponse>({
    queryKey: ["athlete-q-history", limit, offset],
    queryFn: async () => {
      const r = await api.get<AthleteHistoryResponse>(
        "/athlete/profile/questionnaire/history",
        { params: { limit, offset } },
      );
      return r.data;
    },
    enabled,
    // History changes only when athlete edits or coach edits, both of
    // which we already invalidate via the `q-answers` chain. 60s
    // staleTime balances fresh-on-mount vs not-hammering when the
    // athlete scrolls or briefly leaves the page.
    staleTime: 60_000,
  });
}


export function useCompleteQuestionnaire() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<{ ok: boolean; status: AthleteStatus }>("/athlete/profile/questionnaire/complete");
      return r.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["me"], (old: any) => old ? { ...old, status: data.status } : old);
      qc.invalidateQueries({ queryKey: ["q-answers"] });
    },
  });
}
